import { useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import Snackbar from '@mui/material/Snackbar';
import { EventResponse, useCancelEvent, useDeleteEvent } from 'api';
import {
  TextV2,
  SvgTaskChecklistBold,
  SvgSingleNeutralBold,
  SvgCalendar3Bold,
  SvgStyleTwoPinMarkerBold,
  Alert,
  DetailsHeader,
} from '@insights-ltd/design-library/components';
import {
  dateInPastForZone,
  toLocaleString,
} from '@insights-ltd/design-library/utils';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router';
import { EventType } from 'types/types';
import { textKeyForDialect } from 'utils/languageUtils';
import { isExploreExperience, isIdtlExperience } from 'domain/event';
import { TIMEZONE_OPTIONS } from 'domain/timezone';
import { EVENT_TYPES } from 'variables';
import useHistoryState, {
  LocationStateProvider,
} from 'components/hooks/useHistoryState';
import { FREQUENCY_OPTIONS } from 'variables/constants';
import { spacingSizeMap } from '@insights-ltd/design-library/themes';
import { Box, styled } from '@mui/material';
import { useSearchContext } from 'components/SearchFilter/SearchProvider';
import DeleteOrCancelEventDialog from './DeleteOrCancelEventDialog';
import EventDetailGrid from './EventDetailGrid';
import { PhysicalDetails, RemoteDetails } from './EventDetailsLocation';
import { DeleteType } from './types';

type EventSummaryProps = {
  event: EventResponse;
  eventInviteesCount: number;
  eventRefundableUnits: number;
};

const StyledH3 = styled(TextV2)({
  textAlign: 'center',
});

const formatDate = (date: string, dialect = 'en-GB') =>
  toLocaleString(date, 'UTC', dialect, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });

const formatDateTime = (date: string, dialect = 'en-GB') => {
  const time = toLocaleString(date, 'UTC', dialect, {
    hour: 'numeric',
    minute: 'numeric',
    hour12: false,
  }).toLowerCase();
  const day = formatDate(date, dialect);
  return `${day} - ${time}`;
};

const eventTypeKey = (eventType: EventType) => {
  const selectedEventType = EVENT_TYPES[eventType];

  if (!selectedEventType) {
    throw new Error(`Event Type '${eventType}' not recognised`);
  }

  return selectedEventType.textKey;
};

const EventSummary = ({
  event,
  eventInviteesCount,
  eventRefundableUnits,
}: EventSummaryProps) => {
  const { t, i18n } = useTranslation();
  const { pathname } = useLocation();
  const [historyState] = useHistoryState<string>('prev');
  const queryClient = useQueryClient();
  const {
    mutate: deleteEvent,
    isPending: deleteIsPending,
    isError: deleteIsError,
    reset: deleteReset,
  } = useDeleteEvent(queryClient);
  const {
    mutate: cancelEvent,
    isPending: cancelIsPending,
    isError: cancelIsError,
    reset: cancelReset,
  } = useCancelEvent(queryClient);
  const navigate = useNavigate();
  const [deleteDialogOpen, setOpenDeleteDialog] = useState(false);
  const isExplore = isExploreExperience(event.eventType);
  const isPastExperience: boolean = dateInPastForZone(
    event.startsAt,
    event.timezone,
  );
  const isStartDateInPast: boolean = dateInPastForZone(
    event.startsAt,
    event.timezone,
  );
  const isCancelledExperience = event.eventStatus === 'CANCELLED';
  const [eventDeleteType, setEventDeleteType] = useState<DeleteType>('cancel');
  const [refundedPractitioner, setRefundedPractitioner] = useState(
    event.practitioners.length && isExplore ? event.practitioners[0].id : '',
  );

  const isReadOnly = isStartDateInPast || isCancelledExperience;

  const canCancelOrDelete =
    !isCancelledExperience &&
    !dateInPastForZone(event.extendedEndDate, event.timezone);

  const { setSearchState } = useSearchContext();

  function getTimezoneLabel(timezone: string): string {
    const timezoneOption = TIMEZONE_OPTIONS.find(
      (option) => option.value === timezone,
    );

    return timezoneOption ? t(timezoneOption.label) : timezone;
  }

  useEffect(() => {
    let deleteType: DeleteType = 'delete';

    if (isExplore) {
      // If event is Explore type and Invitees count > 0, there are units, and startDate is in past
      if (
        eventInviteesCount > 0 ||
        eventRefundableUnits > 0 ||
        isPastExperience
      ) {
        deleteType = 'cancel';
      }
      // If event is not Explore && Invitees count > 0
    } else if (eventInviteesCount > 0) {
      deleteType = 'cancel';
    }

    setEventDeleteType(deleteType);
  }, [isExplore, eventInviteesCount, eventRefundableUnits, isPastExperience]);

  const frequency = FREQUENCY_OPTIONS.find(
    (option) => option.value === event.notificationFrequency,
  );

  const eventActionState =
    eventDeleteType === 'cancel'
      ? {
          isPending: cancelIsPending,
          isError: cancelIsError,
          reset: cancelReset,
        }
      : {
          isPending: deleteIsPending,
          isError: deleteIsError,
          reset: deleteReset,
        };

  const handleConfirmSuccess = () => {
    if (historyState?.includes('/organisations/')) {
      navigate(`/organisations/${event?.organisation?.id}`, {
        state: {
          activeTab: 'cancelled',
          showDeletedDialog: eventDeleteType,
          experienceName: event.name,
        },
      });
    } else {
      navigate(`/experiences`, {
        state: {
          showDeletedDialog: eventDeleteType,
          experienceName: event.name,
        },
      });
    }
  };

  const handleConfirmDelete = (type: DeleteType) => {
    if (type === 'cancel') {
      cancelEvent(
        {
          eventId: event.id,
          refundedPractitioner,
        },
        {
          onSuccess: () => handleConfirmSuccess(),
        },
      );
      setSearchState({ searchTerm: '', category: 'cancelled' });
    } else {
      deleteEvent(
        {
          eventId: event.id,
        },
        {
          onSuccess: () => handleConfirmSuccess(),
        },
      );
      setSearchState({ searchTerm: '' });
    }
  };

  return (
    <div>
      <StyledH3 variant="h3">
        {t('ui.event-management.events.summary.title')}
      </StyledH3>
      <Box
        sx={{
          mt: 4,
          mb: 1,
        }}
      >
        <DetailsHeader
          id="core-details"
          Icon={SvgTaskChecklistBold}
          iconProps={{ iconColour: 'pink', iconShape: 'circle' }}
          text={t('ui.event-management.events.create.core-details')}
          type={isReadOnly ? 'default' : 'action'}
          actionLabel={t('ui.event-management.events.summary.button.edit')}
          onClick={() => {
            navigate(`${pathname}/edit/core-details`);
          }}
          fullWidth
        />
      </Box>
      <EventDetailGrid
        details={[
          ...(event.organisation
            ? [
                {
                  title: t(
                    'ui.event-management.organisations.add.label.company-name',
                  ),
                  value: event.organisation?.name,
                },
              ]
            : []),
          {
            title: t('ui.event-management.events.create.label.event-name'),
            value: event.name,
          },
          {
            title: t('ui.event-management.events.create.label.event-type'),
            value: t(eventTypeKey(event.eventType)),
          },
        ]}
      />
      <Box
        sx={{
          mt: 4,
          mb: 1,
        }}
      >
        <DetailsHeader
          id="personnel"
          Icon={SvgSingleNeutralBold}
          iconProps={{ iconColour: 'pink', iconShape: 'circle' }}
          text={t(
            'ui.event-management.events.edit.practitioners.summary-header',
            { count: event.practitioners.length },
          )}
          type={isReadOnly ? 'default' : 'action'}
          actionLabel={t('ui.event-management.events.summary.button.edit')}
          onClick={() => {
            navigate(`${pathname}/edit/practitioners`);
          }}
          fullWidth
        />
      </Box>
      <TextV2 component="span">
        {event.practitioners.map((practitioner, index) => (
          <TextV2
            component="span"
            key={`personnel-${practitioner.id}`}
            variant="bodyBold"
            color="textPrimary"
          >
            {practitioner.fullName}
            {index + 1 < event.practitioners.length ? ', ' : ''}
          </TextV2>
        ))}
      </TextV2>
      <Box
        sx={{
          mt: 4,
          mb: 1,
        }}
      >
        <DetailsHeader
          id="event-details"
          Icon={SvgCalendar3Bold}
          iconProps={{ iconColour: 'pink', iconShape: 'circle' }}
          text={t('ui.event-management.events.create.event-details')}
          type={isReadOnly ? 'default' : 'action'}
          actionLabel={t('ui.event-management.events.summary.button.edit')}
          onClick={() => {
            navigate(`${pathname}/edit/event-details`);
          }}
          fullWidth
        />
      </Box>
      <EventDetailGrid
        details={[
          {
            title: t('ui.event-management.events.create.label.event-language'),
            value: t(textKeyForDialect(event.dialect)),
          },
          {
            title: t('ui.event-management.events.create.label.timezone'),
            value: getTimezoneLabel(event.timezone),
          },
          {
            title: t('ui.event-management.events.overview.label.starts'),
            value: formatDateTime(event.startsAt, i18n.language),
          },
          {
            title: t('ui.event-management.events.overview.label.ends'),
            value: formatDateTime(event.endsAt, i18n.language),
          },
          ...(!isIdtlExperience(event.eventType)
            ? [
                {
                  title: t(
                    'ui.event-management.events.create.label.evaluator-deadline',
                  ),
                  value: formatDate(event.deadlineAt, i18n.language),
                },
                {
                  title: t(
                    'ui.event-management.events.create.label.email-updates',
                  ),
                  value: frequency?.textKey ? t(frequency.textKey) : '',
                },
              ]
            : []),
          ...(event.customEmailMessage
            ? [
                {
                  dataTestId: 'custom-email-message',
                  title: t(
                    'ui.event-management.events.create.label.custom-email',
                  ),
                  value:
                    event.customEmailMessage.length > 30
                      ? `${event.customEmailMessage.substring(0, 30)}...`
                      : event.customEmailMessage,
                },
              ]
            : []),
        ]}
      />
      <Box
        sx={{
          mt: 4,
          mb: 1,
        }}
      >
        <DetailsHeader
          id="event-location"
          Icon={SvgStyleTwoPinMarkerBold}
          iconProps={{ iconColour: 'pink', iconShape: 'circle' }}
          text={t('ui.event-management.events.overview.label.event-location')}
          type={isReadOnly ? 'default' : 'action'}
          actionLabel={t('ui.event-management.events.summary.button.edit')}
          onClick={() => {
            navigate(`${pathname}/edit/event-location`);
          }}
          fullWidth
        />
      </Box>
      <TextV2 component="p" variant="bodyBold" color="textSecondary">
        {t('ui.event-management.events.create.location')}
      </TextV2>

      {event.location.type === 'REMOTE' ? (
        <RemoteDetails location={event.location} />
      ) : (
        <PhysicalDetails location={event.location} />
      )}
      {canCancelOrDelete && (
        <Button
          fullWidth
          onClick={() => setOpenDeleteDialog(true)}
          sx={(theme) => ({
            color: theme.palette.error.main,
            borderColor: theme.palette.error.main,
            border: '1px solid',
            backgroundColor: 'transparent',
            marginTop: theme.spacing(spacingSizeMap.L),
            '&:hover': {
              borderColor: theme.palette.error.main,
              backgroundColor: `${theme.palette.error.main}16`,
            },
          })}
          variant="outlined"
        >
          {eventDeleteType === 'cancel'
            ? t('ui.event-management.events.overview.button.cancel-event')
            : t('ui.event-management.events.overview.button.delete-event')}
        </Button>
      )}
      <DeleteOrCancelEventDialog
        open={deleteDialogOpen}
        onClose={() => setOpenDeleteDialog(false)}
        handleConfirmDelete={() => handleConfirmDelete(eventDeleteType)}
        deleteType={eventDeleteType}
        isLoading={eventActionState.isPending}
        event={event}
        availableUnits={eventRefundableUnits}
        setRefundedPractitioner={setRefundedPractitioner}
      />
      <Snackbar
        open={eventActionState.isError}
        autoHideDuration={6000}
        onClose={eventActionState.reset}
      >
        <Alert
          elevation={6}
          variant="filled"
          onClose={eventActionState.reset}
          severity="error"
        >
          {eventDeleteType === 'cancel'
            ? t('ui.event-management.events.error-canceling-event')
            : t('ui.event-management.events.error-deleting-event')}
        </Alert>
      </Snackbar>
    </div>
  );
};

const WrappedEventSummary = ({
  event,
  eventInviteesCount,
  eventRefundableUnits,
}: EventSummaryProps) => (
  <LocationStateProvider>
    <EventSummary
      event={event}
      eventInviteesCount={eventInviteesCount}
      eventRefundableUnits={eventRefundableUnits}
    />
  </LocationStateProvider>
);

export default WrappedEventSummary;
